<template>
  <div class="video-options">
    <div class="video-options__column">
      <div
        v-if="canvasName === 'modules' || isFullSlideAsset"
        class="video-options__option"
      >
        <NebulaCheckbox
          class="video-options__option-checkbox"
          :is-disabled="autoAdvanceDisabled"
        >
          <template #checkbox>
            <input
              class="nebula-checkbox"
              type="checkbox"
              :checked="autoAdvance && !isLastSlide"
              :disabled="autoAdvanceDisabled"
              :aria-label="$t('Auto Advance')"
              @change="onSettingChanged('autoAdvance')"
            >
          </template>
        </NebulaCheckbox>
        <NebulaIcon
          symbol-id="video-advance"
          size="m"
          class="video-options__option-icon"
        />
        <span class="video-options__option-text">{{ $t('Auto Advance') }}</span>
      </div>
      <div class="video-options__option">
        <NebulaCheckbox class="video-options__option-checkbox">
          <template #checkbox>
            <input
              class="nebula-checkbox"
              type="checkbox"
              :checked="autoPlay"
              :aria-label="$t('Auto Play')"
              @change="onSettingChanged('autoPlay')"
            >
          </template>
        </NebulaCheckbox>
        <NebulaIcon
          symbol-id="play"
          size="m"
          class="video-options__option-icon"
        />
        <span class="video-options__option-text">{{ $t('Auto Play') }}</span>
      </div>
      <div class="video-options__option">
        <NebulaCheckbox class="video-options__option-checkbox">
          <template #checkbox>
            <input
              class="nebula-checkbox"
              type="checkbox"
              :checked="loopVideo"
              :disabled="loopVideoDisabled"
              :aria-label="$t('Loop')"
              @change="onSettingChanged('loopVideo')"
            >
          </template>
        </NebulaCheckbox>
        <NebulaIcon
          symbol-id="video-loop"
          size="m"
          class="video-options__option-icon"
        />
        <span class="video-options__option-text">{{ $t('Loop') }}</span>
      </div>
    </div>
    <div class="video-options__column">
      <div class="video-options__option">
        <NebulaInput
          class="video-options__option-input"
          :class="{
            'video-options__option-input-start-error': startTimeValidationError
          }"
          type="text"
          size="s"
          placeholder="00:00"
          :model-value="updateStartTime"
          @input="onStartTimeInput"
          :aria-label="$t('Start Time')"
        />
        <span class="video-options__option-text">{{ $t('Start Time') }}</span>
      </div>
      <div class="video-options__option">
        <NebulaInput
          class="video-options__option-input"
          :class="{
            'video-options__option-input-end-error': endTimeValidationError
          }"
          type="text"
          size="s"
          placeholder="00:00"
          :model-value="updateEndTime"
          @input="onEndTimeInput"
          :aria-label="$t('End Time')"
        />
        <span class="video-options__option-text">{{ $t('End Time') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import {
  cloneDeep, debounce, get, last, snakeCase,
} from 'lodash-es';
import {
  NebulaCheckbox,
  NebulaInput,
  NebulaIcon,
} from '@discoveryedu/nebula-components';
import {
  useEditorStore,
} from '@/stores';
import { durationToSeconds } from '@/lib/utils';
import * as types from '@/lib/constants/store';

export default {
  components: {
    NebulaCheckbox,
    NebulaInput,
    NebulaIcon,
  },
  data() {
    return {
      updateStartTime: get(this, `${this.moduleData ? 'moduleData' : 'page'}.options.video_start`),
      updateEndTime: get(this, `${this.moduleData ? 'moduleData' : 'page'}.options.video_end`),
      startTimeValidationError: false,
      endTimeValidationError: false,
    };
  },
  props: {
    isFullSlideAsset: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Object,
      default: null,
    },
    moduleData: {
      type: Object,
      default: null,
    },
    canvasName: {
      type: String,
      default: null,
    },
  },
  created() {
    this.debounceOnSettingChanged = debounce(this.onSettingChanged, 1000);
  },
  computed: {
    ...mapState(useEditorStore, [
      'pagesSorted',
      'videoDurations',
    ]),
    options() {
      return get(this, `${this.moduleData ? 'moduleData' : 'page'}.options`);
    },
    autoAdvance() {
      return get(this, 'options.auto_advance', false);
    },
    autoAdvanceDisabled() {
      return this.loopVideo || this.isLastSlide;
    },
    isLastSlide() {
      return last(this.pagesSorted).id === this.page?.id;
    },
    autoPlay() {
      return get(this, 'options.auto_play', false);
    },
    loopVideo() {
      return get(this, 'options.loop_video', false);
    },
    loopVideoDisabled() {
      return this.autoAdvance && !this.isLastSlide;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_PAGE_OPTIONS,
      types.UPDATE_MODULE,
    ]),
    onSettingChanged(name, value) {
      const options = cloneDeep(this.options);
      const moduleProperty = snakeCase(name);
      if (['autoAdvance', 'loopVideo', 'autoPlay'].includes(name)) {
        const toggledValue = !this[name];
        options[moduleProperty] = toggledValue;
        if (name === 'autoAdvance' && toggledValue) options.loop_video = false;
        if (name === 'loopVideo' && toggledValue) options.auto_advance = false;
      } else {
        options[moduleProperty] = value;
      }

      if (this.moduleData) {
        // If moduleData was provided, update options on module instead of page.
        const payload = cloneDeep(this.moduleData);
        payload.options = options;
        this[types.UPDATE_MODULE]({ canvasName: this.canvasName, payload });
      } else {
        this[types.UPDATE_PAGE_OPTIONS]({ options });
      }
    },
    validateTime(time) {
      if (!time) return true;
      const regexp = /^[0-9]?[0-9]:[0-9]{2}$/;
      const isMatchFormat = regexp.test(time);
      const durationObj = this.videoDurations[this.options.asset_id];
      let isValid;
      if (durationObj) {
        const timeInSeconds = durationToSeconds.bind({ time: time || '0' })('time');
        const durationInSeconds = durationToSeconds.bind({ duration: durationObj.duration || '0' })('duration');
        const isLessThanMaxDuration = isMatchFormat && timeInSeconds < durationInSeconds;
        isValid = (isMatchFormat && isLessThanMaxDuration);
      } else {
        isValid = isMatchFormat;
      }
      return isValid;
    },
    onStartTimeInput(time) {
      this.updateStartTime = time || undefined;
      this.startTimeValidationError = !this.validateTime(this.updateStartTime);
      if (this.startTimeValidationError) return;
      this.debounceOnSettingChanged('videoStart', this.updateStartTime);
    },
    onEndTimeInput(time) {
      this.updateEndTime = time || undefined;
      this.endTimeValidationError = !this.validateTime(this.updateEndTime);
      if (this.endTimeValidationError) return;
      this.debounceOnSettingChanged('videoEnd', this.updateEndTime);
    },
  },
};
</script>

<style lang="stylus">
.video-options {
  display: flex;
  &__option {
    align-items: center;
    display: flex;
    margin-top: $nebula-space-1x;

    &:not(:first-of-type) {
      margin-top: $nebula-space-2x;
    }
  }

  &__column {
    margin-right: $nebula-space-2x;
  }

  &__option-checkbox,
  &__option-input {
    margin: 0;
  }

  &__option-input {
    height: $nebula-space-3x;
    width: $nebula-space-10x;
  }

  &__option-input-start-error,
  &__option-input-end-error,
  &__option-input-start-error:focus,
  &__option-input-end-error:focus {
    border: 1px solid $nebula-color-error;
  }

  &__option-icon {
    margin-inline-start: $nebula-space-1x;
  }

  &__option-text {
    font-weight: bold;
    margin-inline-start: $nebula-space-1x;
  }
}
</style>
